import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_get_schedule,
  API_get_actual_used_schedule,
  API_get_role_types,
  API_get_actual_sales_daily,
  API_get_forecasted_sales_daily,
  API_get_effective_wages,
} from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  displayName,
  formatCurrency,
  formatHours,
  isNoShowActualShift,
  shiftDurationHours,
  unwrapPayload,
  lookupEffectiveWageOnDate,
} from "./_shared/laborMath";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// Local helpers for endpoints whose URL has "?store_id=" baked in.
function fetchStoreScoped(api, storeId, extraParams = "") {
  const url = `${api}${storeId}${extraParams ? "&" + extraParams : ""}`;
  return axios.get(url, getDefaultAxiosConfig());
}
function fetchStoreScopedRange(api, storeId, startStr, endStr) {
  return fetchStoreScoped(
    api,
    storeId,
    `date_start=${startStr}&date_end=${endStr}`
  );
}

// Pick the first record matching a given date from a list of {date, value}.
function findOnDate(records, dateStr) {
  if (!Array.isArray(records)) return null;
  return records.find((r) => r.date === dateStr) || null;
}

function emptyBucket() {
  return {
    scheduled: { shifts: 0, hours: 0, wages: 0 },
    actual: { shifts: 0, hours: 0, wages: 0 },
  };
}

function rate(wages, hours) {
  if (!hours || hours <= 0) return null;
  return wages / hours;
}

function laborPct(wages, sales) {
  if (!sales || sales <= 0) return null;
  return (wages / sales) * 100;
}

// Bucket header colors / weight — kept consistent across the four sections.
function SectionCard({ title, children, footnote }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
        <h5 className="text-sm font-semibold text-slate-800">{title}</h5>
        {footnote && (
          <p className="text-[10px] text-slate-500 mt-0.5">{footnote}</p>
        )}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

const TH = ({ children, align = "left", colSpan }) => (
  <th
    colSpan={colSpan}
    className={`font-semibold text-slate-700 px-3 py-2 text-${align}`}
  >
    {children}
  </th>
);
const TD = ({ children, align = "left", strong = false }) => (
  <td
    className={`px-3 py-2 text-${align} ${
      strong ? "font-semibold text-slate-900" : "text-slate-700"
    }`}
  >
    {children}
  </td>
);

function SummaryTable({ rows, labelHeader }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-white border-b border-slate-100">
        <tr>
          <TH>{labelHeader}</TH>
          <TH align="right">Sched Shifts</TH>
          <TH align="right">Act Shifts</TH>
          <TH align="right">Sched Hrs</TH>
          <TH align="right">Act Hrs</TH>
          <TH align="right">Est Rate</TH>
          <TH align="right">Act Rate</TH>
          <TH align="right">Proj Wages</TH>
          <TH align="right">Act Wages</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50">
            <TD strong>{r.label}</TD>
            <TD align="right">{r.scheduled.shifts}</TD>
            <TD align="right">{r.actual.shifts}</TD>
            <TD align="right">{formatHours(r.scheduled.hours)}</TD>
            <TD align="right">{formatHours(r.actual.hours)}</TD>
            <TD align="right">
              {rate(r.scheduled.wages, r.scheduled.hours) == null
                ? "—"
                : formatCurrency(rate(r.scheduled.wages, r.scheduled.hours))}
            </TD>
            <TD align="right">
              {rate(r.actual.wages, r.actual.hours) == null
                ? "—"
                : formatCurrency(rate(r.actual.wages, r.actual.hours))}
            </TD>
            <TD align="right">{formatCurrency(r.scheduled.wages)}</TD>
            <TD align="right">{formatCurrency(r.actual.wages)}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DailyLaborScheduleSummaryReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  // Opens on the store's last completed business day: the report is built
  // from actuals, which aren't complete until the business day ends.
  const [selectedDate, setSelectedDate] = useState(() =>
    lastCompletedBusinessDay(storeNames, homeStore)
  );

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
  }, []);

  const dateStr = useMemo(
    () => dayjs(selectedDate).format("YYYY-MM-DD"),
    [selectedDate]
  );
  // For stores whose business day starts after midnight (start_qhnum > 0),
  // overnight punches land on a different CALENDAR date than the business
  // date they belong to. get_actual_used_schedule filters by calendar
  // date_start, so we widen the fetch window by ±1 day and trim back to
  // the requested business date in JS using each row's business_date
  // field. For start_qhnum=0 stores this is a no-op — business_date
  // equals date_start for every row.
  const actualFetchStart = useMemo(
    () => dayjs(selectedDate).subtract(1, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );
  const actualFetchEnd = useMemo(
    () => dayjs(selectedDate).add(1, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );

  const [schedule, setSchedule] = useState([]);
  const [actual, setActual] = useState([]);
  // Payroll-grade wage data from get_effective_wages — range-aware so that a
  // single-day report just gets one pay-period snapshot. Shape:
  // { effective_wages_by_pay_period, pay_period_by_date }.
  const [wageData, setWageData] = useState(null);
  const [roleTypeMap, setRoleTypeMap] = useState(new Map()); // id → name
  const [actualSales, setActualSales] = useState(null);
  const [forecastSales, setForecastSales] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const results = Promise.allSettled([
      getRightworkAPI(API_get_schedule, {
        store_id: homeStore,
        lower_bound_date: dateStr,
        upper_bound_date: dateStr,
        should_return_all_stores: false,
      }),
      fetchStoreScopedRange(
        API_get_actual_used_schedule,
        homeStore,
        actualFetchStart,
        actualFetchEnd
      ),
      getRightworkAPI(API_get_effective_wages, {
        store_id: homeStore,
        date_start: dateStr,
        date_end: dateStr,
      }),
      fetchStoreScoped(API_get_role_types, homeStore),
      fetchStoreScopedRange(
        API_get_actual_sales_daily,
        homeStore,
        dateStr,
        dateStr
      ),
      fetchStoreScopedRange(
        API_get_forecasted_sales_daily,
        homeStore,
        dateStr,
        dateStr
      ),
    ]);

    (async () => {
      const [
        scheduleRes,
        actualRes,
        wagesRes,
        roleTypesRes,
        actualSalesRes,
        forecastRes,
      ] = await results;

      if (cancelled) return;

      // Schedule is load-bearing — if it fails, surface the error.
      if (scheduleRes.status === "rejected") {
        setError("Couldn't load the schedule. Try again.");
        setIsLoading(false);
        return;
      }
      const schedulePayload = unwrapPayload(scheduleRes.value);
      const scheduleList = Array.isArray(schedulePayload?.schedule)
        ? schedulePayload.schedule
        : Array.isArray(schedulePayload)
        ? schedulePayload
        : [];
      setSchedule(scheduleList);

      // Actuals — non-fatal if missing.
      const actualList =
        actualRes.status === "fulfilled" && Array.isArray(actualRes.value?.data)
          ? actualRes.value.data
          : [];
      setActual(actualList);

      // Payroll-grade wage data. Non-fatal if it fails — wages render as
      // $0 / 0% across the section cards in that case.
      setWageData(wagesRes.status === "fulfilled" ? wagesRes.value?.data ?? null : null);

      // role_type id → name lookup.
      const roleTypeMapNext = new Map();
      if (roleTypesRes.status === "fulfilled") {
        const payload = unwrapPayload(roleTypesRes.value);
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.role_types)
          ? payload.role_types
          : [];
        for (const rt of list) {
          if (rt?.id != null) {
            roleTypeMapNext.set(rt.id, rt.role_type || rt.name || "—");
          }
        }
      }
      setRoleTypeMap(roleTypeMapNext);

      // Sales (DOB row) — null if not available.
      setActualSales(
        actualSalesRes.status === "fulfilled"
          ? findOnDate(unwrapPayload(actualSalesRes.value), dateStr)?.value ??
              null
          : null
      );
      setForecastSales(
        forecastRes.status === "fulfilled"
          ? findOnDate(unwrapPayload(forecastRes.value), dateStr)?.value ??
              null
          : null
      );

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, dateStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Payroll-grade wage resolver: role-specific override first, falls back to
  // the employee's general wage. Returns 0 when no hourly wage is on file so
  // wage totals remain numeric. Matches the Paylocity export's precedence.
  // Per-date so a range crossing pay periods costs each day with the right
  // snapshot (semi-monthly pay periods within a weekly business week).
  const wageFor = useCallback(
    (stmId, scheduledRoleId, dateStr) => {
      const w = lookupEffectiveWageOnDate(
        wageData,
        dateStr,
        stmId,
        scheduledRoleId
      );
      if (w == null) return 0;
      const n = Number(w);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    [wageData]
  );

  // Aggregate every shift across four buckets in one pass.
  const aggregates = useMemo(() => {
    const byEmployee = new Map();
    const byJob = new Map();
    const byWorkCenter = new Map();
    const total = emptyBucket();

    const ensure = (map, key, label, getLabel) => {
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: label ?? (getLabel ? getLabel() : key),
          ...emptyBucket(),
        });
      }
      return map.get(key);
    };

    // Scheduled side
    for (const s of schedule) {
      const stm = s.store_team_member;
      if (!stm) continue;
      const hours = Number(s.hours ?? s.shift_length ?? 0);
      if (Number.isNaN(hours)) continue;
      // role_id can be either an int or an object with .id depending on the
      // serializer; coerce so the wage lookup works either way.
      const scheduledRoleId =
        typeof s.role_id === "object" ? s.role_id?.id ?? null : s.role_id ?? null;
      const wage = wageFor(stm.id, scheduledRoleId, s.date || dateStr);
      const wages = wage * hours;
      const jobName = s.role_id?.role_name || "Unassigned";
      const wcName =
        roleTypeMap.get(s.role_id?.role_type) ||
        roleTypeMap.get(s.role_id?.role_type_id) ||
        "—";

      const emp = ensure(byEmployee, stm.id, displayName(stm));
      emp.scheduled.shifts += 1;
      emp.scheduled.hours += hours;
      emp.scheduled.wages += wages;

      const job = ensure(byJob, jobName, jobName);
      job.scheduled.shifts += 1;
      job.scheduled.hours += hours;
      job.scheduled.wages += wages;

      const wc = ensure(byWorkCenter, wcName, wcName);
      wc.scheduled.shifts += 1;
      wc.scheduled.hours += hours;
      wc.scheduled.wages += wages;

      total.scheduled.shifts += 1;
      total.scheduled.hours += hours;
      total.scheduled.wages += wages;
    }

    // Actual side
    for (const a of actual) {
      // Skip no-show placeholder rows — they represent a scheduled shift
      // that didn't get punched, not real work. The variance vs. the
      // scheduled side already exposes the absence.
      if (isNoShowActualShift(a)) continue;
      // Trim the ±1 day over-fetch back to the requested business date.
      // For stores with start_qhnum=0, business_date equals date_start
      // for every row so this is a no-op.
      const aBusinessDate = a.business_date || a.date_start;
      if (aBusinessDate !== dateStr) continue;
      const stm =
        a.store_team_member || a.published?.store_team_member || null;
      const stmId = stm?.id ?? a.published?.store_team_member?.id ?? null;
      const hours =
        shiftDurationHours(a.shift_start, a.shift_end, a.total_break) ?? 0;
      // Prefer the role the employee actually clocked into (from the
      // linked time entry); fall back to the scheduled role. This matches
      // how the Daily Punch Audit / Paylocity export look up role-specific
      // wages, so role-swap shifts get the right rate. Job grouping below
      // still uses the published role — the planning view stays anchored
      // to "what we planned," but Act Wages reflect what payroll will pay.
      const rawScheduledRoleId = a.published?.role_id;
      const scheduledRoleId =
        typeof rawScheduledRoleId === "object"
          ? rawScheduledRoleId?.id ?? null
          : rawScheduledRoleId ?? null;
      const wageRoleId = a.actual_role_id ?? scheduledRoleId;
      const wage = wageFor(stmId, wageRoleId, aBusinessDate || dateStr);
      const wages = wage * hours;
      const jobName = a.published?.role_id?.role_name || "Unassigned";
      const wcName =
        roleTypeMap.get(a.published?.role_id?.role_type) ||
        roleTypeMap.get(a.published?.role_id?.role_type_id) ||
        "—";

      if (stmId != null) {
        const emp = ensure(
          byEmployee,
          stmId,
          stm ? displayName(stm) : "—"
        );
        emp.actual.shifts += 1;
        emp.actual.hours += hours;
        emp.actual.wages += wages;
      }

      const job = ensure(byJob, jobName, jobName);
      job.actual.shifts += 1;
      job.actual.hours += hours;
      job.actual.wages += wages;

      const wc = ensure(byWorkCenter, wcName, wcName);
      wc.actual.shifts += 1;
      wc.actual.hours += hours;
      wc.actual.wages += wages;

      total.actual.shifts += 1;
      total.actual.hours += hours;
      total.actual.wages += wages;
    }

    return {
      employees: Array.from(byEmployee.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      jobs: Array.from(byJob.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      workCenters: Array.from(byWorkCenter.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      total,
    };
  }, [schedule, actual, wageFor, roleTypeMap]);

  const filters = (
    <div className="flex items-center">
      <DateSelector date={selectedDate} increment_day={incrementDay} />
    </div>
  );

  const fullDateLabel = dayjs(selectedDate).format("dddd, MMMM D, YYYY");

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : aggregates.employees.length === 0 && aggregates.total.actual.shifts === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No scheduled or actual shifts on this day.
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">
              {fullDateLabel}
            </h4>
            <p className="text-xs text-slate-500">
              Scheduled vs. actual hours and wages, summarized four ways.
              Wages are estimates at each employee's effective hourly rate
              (no OT/DT multipliers applied). For payroll-grade per-shift
              pay including overtime and premiums, use the Daily Punch Audit.
            </p>
          </div>

          <SectionCard title="Employee Summary">
            <SummaryTable
              rows={aggregates.employees}
              labelHeader="Employee"
            />
          </SectionCard>

          <SectionCard title="Job Summary">
            <SummaryTable rows={aggregates.jobs} labelHeader="Job" />
          </SectionCard>

          <SectionCard
            title="Work Center Summary"
            footnote={
              roleTypeMap.size === 0
                ? "Work center names couldn't be resolved — showing role-type ids."
                : null
            }
          >
            <SummaryTable
              rows={aggregates.workCenters}
              labelHeader="Work Center"
            />
          </SectionCard>

          <SectionCard
            title="Day of Business Summary"
            footnote="Totals across the day, plus projected vs. actual sales and labor %."
          >
            <table className="w-full text-xs">
              <thead className="bg-white border-b border-slate-100">
                <tr>
                  <TH align="right">Sched Shifts</TH>
                  <TH align="right">Act Shifts</TH>
                  <TH align="right">Sched Hrs</TH>
                  <TH align="right">Act Hrs</TH>
                  <TH align="right">Proj Wages</TH>
                  <TH align="right">Act Wages</TH>
                  <TH align="right">Proj Sales</TH>
                  <TH align="right">Act Sales</TH>
                  <TH align="right">Proj Labor %</TH>
                  <TH align="right">Act Labor %</TH>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50 font-semibold">
                  <TD align="right" strong>
                    {aggregates.total.scheduled.shifts}
                  </TD>
                  <TD align="right" strong>
                    {aggregates.total.actual.shifts}
                  </TD>
                  <TD align="right" strong>
                    {formatHours(aggregates.total.scheduled.hours)}
                  </TD>
                  <TD align="right" strong>
                    {formatHours(aggregates.total.actual.hours)}
                  </TD>
                  <TD align="right" strong>
                    {formatCurrency(aggregates.total.scheduled.wages)}
                  </TD>
                  <TD align="right" strong>
                    {formatCurrency(aggregates.total.actual.wages)}
                  </TD>
                  <TD align="right" strong>
                    {forecastSales == null
                      ? "—"
                      : formatCurrency(forecastSales)}
                  </TD>
                  <TD align="right" strong>
                    {actualSales == null ? "—" : formatCurrency(actualSales)}
                  </TD>
                  <TD align="right" strong>
                    {laborPct(
                      aggregates.total.scheduled.wages,
                      forecastSales
                    ) == null
                      ? "—"
                      : `${laborPct(
                          aggregates.total.scheduled.wages,
                          forecastSales
                        ).toFixed(1)}%`}
                  </TD>
                  <TD align="right" strong>
                    {laborPct(aggregates.total.actual.wages, actualSales) ==
                    null
                      ? "—"
                      : `${laborPct(
                          aggregates.total.actual.wages,
                          actualSales
                        ).toFixed(1)}%`}
                  </TD>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </>
      )}
    </PrebuiltReportShell>
  );
}
